/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type {
	EntryPoint,
	EntryPointComponent,
	JSResourceReference,
	PreloadProps,
} from 'react-relay';
import type { OperationType } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import type { NullableEntryPoint } from '@atlassian/react-entrypoint';
import type { RouterContext } from '@atlassian/react-resource-router';
import { topNavEntryPoint } from '@jira/navigation-apps__atlassian-navigation/src/topNavEntryPoint.entrypoint.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { AnyEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
import {
	PREVIEW_PANEL,
	HORIZONTAL_NAVIGATION,
	MAIN_CONTENT,
	TOP_NAVIGATION,
	RIGHT_SIDEBAR,
	LEFT_SIDEBAR,
	REDIRECT,
} from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';

type ChromelessPage = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	main: NullableEntryPoint<EntryPointComponent<any, any, any, any>, EntryPointRouteParams>;
};

type Page = ChromelessPage & {
	previewPanel?: AnyEntryPoint;
	horizontalNavigation?: AnyEntryPoint;
	horizontalNavigationFallback?: () => JSX.Element;
	leftSidebar?: {
		sidebar: AnyEntryPoint;
		entryPoints?: Record<string, { entryPoint: AnyEntryPoint }>;
		isAppsExpanded?: boolean;
		isFiltersExpanded?: boolean;
		isPlansExpanded?: boolean;
		isProjectsExpanded?: boolean;
		isOperationsExpanded?: boolean;
	};
	rightSidebar?: AnyEntryPoint;
	rightSidebarFallback?: () => JSX.Element;
	redirect?: AnyEntryPoint;
};

export const createChromelessEntryPoint = (
	page: ChromelessPage,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
		};
	};
	toString: () => string;
} => ({
	root: page.main.root,
	getPreloadProps: (
		entryPointParams: EntryPointRouteParams,
	): {
		entryPoints: {
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
		};
	} => ({
		entryPoints: {
			[MAIN_CONTENT]: {
				entryPoint: page.main,
				entryPointParams,
			},
		},
	}),
	toString: () => 'ChromelessEntryPoint',
});

export const createPageEntryPoint = (
	page: Page,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
		};
	};
	toString: () => string;
} => ({
	root: page.main.root,
	getPreloadProps: (
		entryPointParams: EntryPointRouteParams,
	): {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
		};
	} => ({
		entryPoints: {
			[MAIN_CONTENT]: {
				entryPoint: page.main,
				entryPointParams,
			},
			[TOP_NAVIGATION]: {
				entryPoint: topNavEntryPoint,
				entryPointParams,
			},
			...(page.leftSidebar
				? {
						[LEFT_SIDEBAR]: {
							entryPoint: page.leftSidebar.sidebar,
							entryPointParams: {
								...entryPointParams,
								entryPoints: {
									...(page.leftSidebar.entryPoints
										? injectEntryPointParams(page.leftSidebar.entryPoints, entryPointParams)
										: undefined),
								},
								isAppsExpanded: page.leftSidebar.isAppsExpanded ?? false,
								isFiltersExpanded: page.leftSidebar.isFiltersExpanded ?? false,
								isPlansExpanded: page.leftSidebar.isPlansExpanded ?? false,
								isProjectsExpanded: page.leftSidebar.isProjectsExpanded ?? false,
								isOperationsExpanded: page.leftSidebar.isOperationsExpanded ?? false,
							},
						},
					}
				: undefined),
			...(page.previewPanel && fg('nin_glance_panel_technical_changes')
				? {
						[PREVIEW_PANEL]: {
							entryPoint: page.previewPanel,
							entryPointParams,
						},
					}
				: undefined),
			...(page.horizontalNavigation
				? {
						[HORIZONTAL_NAVIGATION]: {
							entryPoint: page.horizontalNavigation,
							entryPointParams: {
								...entryPointParams,
								fallback: page.horizontalNavigationFallback,
							},
						},
					}
				: undefined),
			...(page.rightSidebar
				? {
						[RIGHT_SIDEBAR]: {
							entryPoint: page.rightSidebar,
							entryPointParams: { ...entryPointParams, fallback: page.rightSidebarFallback },
						},
					}
				: undefined),
			...(page.redirect
				? {
						[REDIRECT]: {
							entryPoint: page.redirect,
							entryPointParams,
						},
					}
				: undefined),
		},
	}),
	toString: () => 'PageEntryPoint',
});

function injectEntryPointParams(
	obj: Record<string, { entryPoint: AnyEntryPoint }>,
	entryPointParams: EntryPointRouteParams,
): Record<string, { entryPoint: AnyEntryPoint; entryPointParams: EntryPointRouteParams }> {
	const newParams: Record<
		string,
		{ entryPoint: AnyEntryPoint; entryPointParams: EntryPointRouteParams }
	> = {};

	// https://stackoverflow.com/a/16735184
	for (const prop in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, prop)) {
			newParams[prop] = { entryPoint: obj[prop].entryPoint, entryPointParams };
		}
	}
	return newParams;
}
